import { createContext, PropsWithChildren, useContext, useRef } from 'react';
import type { TemporalState } from 'zundo';
import { temporal } from 'zundo';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';

import {
  addConditionToGroup,
  addGroupToGroup,
  canUseVariantSchemaOperator,
  createDefaultRoot,
  deleteNodeById,
  findNodeById,
  findParentGroupId,
  isLockedBaseSegmentCondition,
  jsonToTree,
  moveNodeInTree,
  normalizeVariantSchemaUsage,
  reorderWithinGroup,
  treeToJSON,
  updateConditionNode,
  updateGroupOperator,
  validateTree,
} from './utils';

import type {
  TConditionNode,
  TConditionsOutput,
  TGroupNode,
  TLogicalOperator,
  TRuleBuilderKind,
} from './types';

const VARIANT_SCHEMA_OPERATOR = 'variantSchema';

// ─── Store types ─────────────────────────────────────────────────────────────

/** Validation state: controls whether invalid fields are visually highlighted. */
type TValidationState = {
  /** Whether the user has triggered validation (e.g. on form submit). */
  showValidation: boolean;
  /** Set of node IDs that failed validation. */
  invalidNodeIds: Set<string>;
  /** Monotonic counter incremented on each explicit validation run. */
  runCount: number;
};

/** Slice of state stored in the Zustand store. */
type TState = {
  /** Rule kind that controls conditional operator availability. */
  kind: TRuleBuilderKind;
  /** Optional logical operator that locks the root group. */
  fixedRootOperator?: TLogicalOperator;
  /** Root of the rule tree (always a group node). */
  root: TGroupNode;
  /** Current validation state. */
  validation: TValidationState;
};

/** Slice of actions available in the Zustand store. */
type TAction = {
  /** Appends a new empty condition to the given group. */
  addCondition: (groupId: string) => void;
  /** Appends a new nested group to the given group. */
  addGroup: (groupId: string) => void;
  /** Removes a node (condition or group) by its ID from the tree. */
  deleteNode: (nodeId: string) => void;
  /** Partially updates the fields of a condition node. */
  updateCondition: (nodeId: string, updates: Partial<TConditionNode>) => void;
  /** Changes the logical operator of a group node. */
  updateOperator: (groupId: string, operator: TLogicalOperator) => void;
  /** Moves a node to a different group at a specific index. */
  moveNode: (nodeId: string, targetGroupId: string, targetIndex: number) => void;
  /** Reorders children within the same group by swapping old and new indices. */
  reorderGroup: (groupId: string, oldIndex: number, newIndex: number) => void;
  /** Returns the ID of the direct parent group for a given node. */
  findParent: (nodeId: string) => string | null;
  /** Reverts the last mutation to the rule tree. */
  undo: VoidFunction;
  /** Re-applies the last undone mutation to the rule tree. */
  redo: VoidFunction;
  /**
   * Triggers visual validation of the entire rule tree.
   * Highlights all invalid nodes and returns whether the tree is valid.
   */
  triggerValidation: () => { isRuleValid: boolean };
  /** Serializes the current tree to a `json-rules-engine`-compatible JSON object. */
  getJSON: () => TConditionsOutput;
};

type TStore = TState & TAction;
type TTemporalStore = StoreApi<TemporalState<Pick<TState, 'root'>>>;
type TStoreContext = StoreApi<TStore> & { temporal: TTemporalStore };

const StoreContext = createContext<TStoreContext | null>(null);

/**
 * Creates a scoped Zustand store for a single `RuleBuilder` instance.
 *
 * @param initialConditions - Optional initial `TConditionsOutput` to populate the tree.
 * @param onChangeRef - Stable ref to the `onChange` callback, updated on every render
 *   to avoid stale closures without recreating the store.
 */
function createRuleBuilderStore(
  initialConditions: TConditionsOutput | undefined,
  kind: TRuleBuilderKind,
  fixedRootOperator: TLogicalOperator | undefined,
  onChangeRef: React.RefObject<((value: TConditionsOutput) => void) | undefined | null>,
): TStoreContext {
  const parsedInitialRoot = initialConditions ? jsonToTree(initialConditions) : createDefaultRoot();
  const initialRoot = {
    ...normalizeVariantSchemaUsage(parsedInitialRoot, kind),
    ...(fixedRootOperator ? { operator: fixedRootOperator } : {}),
  };

  const store = createStore<TStore>()(
    temporal(
      (set, get, api) => ({
        kind,
        fixedRootOperator,
        root: initialRoot,
        validation: { showValidation: false, invalidNodeIds: new Set(), runCount: 0 },

        addCondition: (groupId) =>
          set((s) => {
            const root = addConditionToGroup(
              s.root,
              groupId,
              s.kind === 'rule-with-variants' ? true : undefined,
            );
            return { root };
          }),

        addGroup: (groupId) =>
          set((s) => {
            const root = addGroupToGroup(
              s.root,
              groupId,
              s.kind === 'rule-with-variants' ? true : undefined,
            );
            return { root };
          }),

        deleteNode: (nodeId) =>
          set((s) => {
            const nodeToDelete = findNodeById(s.root, nodeId);
            if (isLockedBaseSegmentCondition(nodeToDelete, s.kind)) {
              return s;
            }

            const root = deleteNodeById(s.root, nodeId);
            return { root };
          }),

        updateCondition: (nodeId, updates) =>
          set((s) => {
            const currentNode = findNodeById(s.root, nodeId);
            if (isLockedBaseSegmentCondition(currentNode, s.kind)) {
              return s;
            }

            if (
              updates.operator === VARIANT_SCHEMA_OPERATOR &&
              !canUseVariantSchemaOperator(s.root, nodeId, s.kind)
            ) {
              return s;
            }

            const root = updateConditionNode(s.root, nodeId, updates);
            // Silence the error highlight for this node as soon as the user starts editing it.
            // Also reset showValidation so "Regla válida" is not shown until isValid() is called explicitly.
            if (s.validation.invalidNodeIds.has(nodeId)) {
              const invalidNodeIds = new Set(s.validation.invalidNodeIds);
              invalidNodeIds.delete(nodeId);
              return {
                root,
                validation: {
                  showValidation: false,
                  invalidNodeIds,
                  runCount: s.validation.runCount,
                },
              };
            }
            return { root };
          }),

        updateOperator: (groupId, operator) =>
          set((s) => {
            if (groupId === s.root.id && s.fixedRootOperator) {
              return s;
            }

            const root = updateGroupOperator(s.root, groupId, operator);
            return { root };
          }),

        moveNode: (nodeId, targetGroupId, targetIndex) =>
          set((s) => {
            const movingNode = findNodeById(s.root, nodeId);
            if (isLockedBaseSegmentCondition(movingNode, s.kind)) {
              return s;
            }

            if (
              movingNode?.type === 'condition' &&
              movingNode.operator === VARIANT_SCHEMA_OPERATOR
            ) {
              const targetIsRoot = targetGroupId === s.root.id;
              const rootHasAnotherVariantSchema = s.root.children.some(
                (child) =>
                  child.type === 'condition' &&
                  child.operator === VARIANT_SCHEMA_OPERATOR &&
                  child.id !== movingNode.id,
              );
              if (s.kind !== 'rule-with-variants' || !targetIsRoot || rootHasAnotherVariantSchema) {
                return s;
              }
            }

            const targetGroup = findNodeById(s.root, targetGroupId);
            // Reject drop into a 'not' group that already has a child
            if (
              targetGroup?.type === 'group' &&
              targetGroup.operator === 'not' &&
              targetGroup.children.length >= 1
            ) {
              return s;
            }
            const root = moveNodeInTree(s.root, nodeId, targetGroupId, targetIndex);
            return { root };
          }),

        reorderGroup: (groupId, oldIndex, newIndex) =>
          set((s) => {
            const group = findNodeById(s.root, groupId);
            if (group?.type !== 'group') {
              return s;
            }

            const movingNode = group.children[oldIndex];
            if (isLockedBaseSegmentCondition(movingNode, s.kind)) {
              return s;
            }

            const root = reorderWithinGroup(s.root, groupId, oldIndex, newIndex);
            return { root };
          }),

        undo: () => (api as unknown as TStoreContext).temporal.getState().undo(),
        redo: () => (api as unknown as TStoreContext).temporal.getState().redo(),

        findParent: (nodeId) => findParentGroupId(get().root, nodeId),

        triggerValidation: () => {
          const { isRuleValid, invalidIds } = validateTree(get().root, get().kind);
          set((s) => ({
            validation: {
              showValidation: true,
              invalidNodeIds: invalidIds,
              runCount: s.validation.runCount + 1,
            },
          }));
          return { isRuleValid };
        },

        getJSON: () => treeToJSON(get().root),
      }),
      { partialize: (s) => ({ root: s.root }), limit: 100 },
    ),
  ) as TStoreContext;

  // Notify onChange for any root change, including undo/redo restorations.
  let prevRoot = store.getState().root;
  store.subscribe((state) => {
    if (state.root !== prevRoot) {
      prevRoot = state.root;
      onChangeRef.current?.(treeToJSON(state.root));
    }
  });

  return store;
}

/**
 * RuleBuilderProvider
 *
 * Context provider that creates and scopes a Zustand store for a single
 * `RuleBuilder` instance. Must wrap all components that call `useRuleBuilderStore`.
 *
 * The store is created only once on mount from `defaultValue`. Subsequent changes
 * to `defaultValue` are ignored — to fully reset the editor, change its `key` prop.
 * The `onChange` callback is stored in a ref so it is always up-to-date without
 * re-creating the store.
 *
 * @param defaultValue - Optional initial `TConditionsOutput` for the rule tree.
 * @param onChange - Callback fired on every change with the serialized conditions.
 */
export function RuleBuilderProvider({
  children,
  defaultValue,
  fixedRootOperator,
  kind = 'rule',
  onChange,
}: PropsWithChildren<{
  defaultValue?: TConditionsOutput;
  fixedRootOperator?: TLogicalOperator;
  kind?: TRuleBuilderKind;
  onChange?: (value: TConditionsOutput) => void;
}>) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const storeRef = useRef<TStoreContext | null>(null);
  if (!storeRef.current) {
    storeRef.current = createRuleBuilderStore(defaultValue, kind, fixedRootOperator, onChangeRef);
  }

  return <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>;
}

/**
 * useRuleBuilderStore
 *
 * Typed selector hook for the scoped RuleBuilder Zustand store.
 * Must be called within a component tree wrapped by `RuleBuilderProvider`.
 *
 * @param selector - Function that picks a slice of the store state.
 * @returns The selected slice, re-rendering the component only when it changes.
 * @throws If called outside of a `RuleBuilderProvider`.
 */
export function useRuleBuilderStore<T>(selector: (state: TStore) => T): T {
  const storeContext = useContext(StoreContext);
  if (!storeContext) throw new Error('useRuleBuilderStore must be used inside RuleBuilderProvider');
  return useStore(storeContext, selector);
}

/**
 * useRuleBuilderTemporalStore
 *
 * Selector hook for the temporal (undo/redo) store powered by `zundo`.
 * Use this to read `pastStates` and `futureStates` for enabling/disabling
 * undo and redo controls.
 *
 * @param selector - Function that picks a slice of the temporal state.
 * @throws If called outside of a `RuleBuilderProvider`.
 */
export function useRuleBuilderTemporalStore<T>(
  selector: (state: TemporalState<Pick<TState, 'root'>>) => T,
): T {
  const storeContext = useContext(StoreContext);
  if (!storeContext)
    throw new Error('useRuleBuilderTemporalStore must be used inside RuleBuilderProvider');
  return useStore(storeContext.temporal, selector);
}

// ─── Disabled context ─────────────────────────────────────────────────────────

const DisabledContext = createContext(false);

/** Context provider used by `RuleBuilder` to broadcast the `disabled` flag to all nested components. */
export const RuleBuilderDisabledProvider = DisabledContext.Provider;

/**
 * useRuleBuilderDisabled
 *
 * Returns `true` when the enclosing `RuleBuilder` is in read-only / disabled mode.
 * Must be called within a component tree rendered by `RuleBuilder`.
 */
export function useRuleBuilderDisabled(): boolean {
  return useContext(DisabledContext);
}
